import Phaser from 'phaser';
import AContainer from './AContainer.js';
import AText from './AText.js';
import AImage from './AImage.js';

export default class AScene extends Phaser.Scene
{
    constructor(sceneName)
    {
        super(sceneName);

        this.defaultAtlas = 'UIAssets';
        this.defaultAudioSprite = 'gameAudio';
    }

    preload()
    {
        this.shadowObjects = [];
    }
    
    addRef(key)
    {
        this.addImage(this.mid.x, this.mid.y, key).setScale(1).setAlpha(.5).setDepth(9999).name = "tempRef";
    }

    /**
     * Adds a Mask
     * @return {Phaser.Display.Masks.GeometryMask} 
     */
    addSquareMask(x, y, width, height, visible)
    {
        let shape = this.addRect(x, y, width, height, 0xff0).setVisible(visible);
        let mask = new Phaser.Display.Masks.GeometryMask(this, shape);
        return mask;
    }

    /**
     * Adds a Mask.
     * @return {Phaser.GameObjects.Graphics} 
     */
    addPathMask(child, x, y, points, debug = false)
    {
        let graphics = this.addPolygon(x, y, points, 0xff0000);
        graphics.visible = debug;
        this.add.existing(graphics);
        if(debug) graphics.setDepth(10000);

        if(child)
        {
            if (child instanceof Array) child.forEach((kid) => { kid.mask = new Phaser.Display.Masks.BitmapMask(this, graphics); });
            else child.mask = new Phaser.Display.Masks.BitmapMask(this, graphics);
        }

        return graphics;
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addPolygon(x, y, points, color = 0xffffff, parent = null, completeShape = true)
    {
        let poly = new Phaser.Geom.Polygon(points);

        let graphics = this.make.graphics();
        graphics.points = poly.points;
        graphics.x = x;
        graphics.y = y;
        graphics.fillStyle(color);
        graphics.fillPoints(poly.points, completeShape);

        this.addToParent(graphics, parent);
        return graphics;
    }

    /**
     * @param {AContainer} container 
     * @param {AContainer} parent 
     * @return {AContainer}
     */
    addContainer(x, y, container, parent = null)
    {
        if(container == null) container = new Phaser.GameObjects.Container(this, 0, 0);
        let cont = container;
        cont.setPosition(x, y);
        this.addToParent(cont, parent);
        return cont;
    }

    addText(x, y, textSize, hexColor, font, text = "", parent = null, alignMiddle = true)
    {
        let textField = new AText(this, x, y, textSize, hexColor, font, text);
        if(alignMiddle)
        {
            textField.setOrigin(.5);
            textField.setAlign('center');
        }
        this.addToParent(textField, parent);
        return textField;
    }

    /** 
     * @return {AImage}
     */
    addImage(x, y, key, parent = null, atlas = this.defaultAtlas)
    {
        let image = new AImage(this, x, y, atlas, key, null);
        this.addToParent(image, parent);
        return image;
    }

    addInputField(x, y, width, height, fontSize, fontColor, font, maxCharacters = 50, parent = null, debug = false)
    {
        let inputField = new InputField(this, x, y, width, height, fontSize, fontColor, font, maxCharacters, debug);
        this.addToParent(inputField, parent);
        return inputField;
    }

    addDomInputField(x, y, width, height, fontSize, fontColor, font, maxCharacters = 50, parent = null, debug = false)
    {
        let inputField = new DomInputField(this, x, y, width, height, fontSize, fontColor, font, maxCharacters, debug);
        this.addToParent(inputField.display, parent);
        return inputField;
    }

    /** 
     * @return {InteractableObject}
     */
    addInteractableObject(x, y, key, origin = .5, parent = null, activeCoords)
    {
        let image = new InteractableObject(this, x, y, this.defaultAtlas, key, origin, activeCoords);
        this.addToParent(image, parent);
        return image;
    }

    addDelay(delay, func, args = null)
    {
        return this.time.delayedCall(delay, func, args);
    }

    addRepeatingEvent(delay, event, timesToRepeat = -1)
    {
        return this.time.addEvent({ delay: delay, callback: event, repeat: timesToRepeat });
    }

    /**
     * @param {Phaser.Math.Easing} ease 
     * @param {Phaser.GameObjects.GameObject} object 
     * @return {Phaser.Tweens.Tween}
     */
    addTween(object, duration, parameters, ease = null, onComplete = null)
    {
        if(object == null) return null;

        parameters.targets = object;
        parameters.ease = ease;
        parameters.duration = duration;
        parameters.onComplete = onComplete;

        if(parameters.onStart && parameters.delay)
        {
            let startFunc = parameters.onStart;
            delete parameters.onStart;
            this.addDelay(parameters.delay, startFunc);
        }
        if(parameters.tint != null)
        {
            this.tweenTint(object, parameters.tint, duration);
            delete parameters.tint;
        }
        
        let tween = this.tweens.add(parameters);

        return tween;
    }

    /**
     * @param {AImage} image 
     * @param {Phaser.Display.Color} endColor 
     */
    tweenTint(image, endColor, duration) 
    {    
        let startColor = Phaser.Display.Color.IntegerToColor(image.tintData ? image.tintData : image.tintBottomLeft);
        endColor = Phaser.Display.Color.IntegerToColor(endColor);
        let tween = this.tweens.addCounter({from: 0, to: 100, duration: duration, onUpdate: ()=>{
            let col = Phaser.Display.Color.Interpolate.ColorWithColor(startColor, endColor, 100, tween.getValue());
            image.tint = Phaser.Display.Color.ObjectToColor(col).color;
        }});
    }

    interpolateColors(color1, color2, interp)
    {
        let startColor = Phaser.Display.Color.IntegerToColor(color1);
        let endColor = Phaser.Display.Color.IntegerToColor(color2);
        let col = Phaser.Display.Color.Interpolate.ColorWithColor(startColor, endColor, 100, interp * 100);
        return Phaser.Display.Color.ObjectToColor(col);
    }

    /**
     * Moves object along a path. You can specify segments in the parameters options like so:
     * ```segments: [
     *      {ease: Phaser.Math.Easing.Sine.In, normDuration: .5, timeScale: 3}, 
     *      {ease: Phaser.Math.Easing.Bounce.Out}
     * ]```
     */
    addPathTween(object, path, duration, parameters = {}, ease = null, onComplete = null)
    {
        let follower = { t: 0, vec: new Phaser.Math.Vector2() };

        parameters.ease = parameters.easeSegments ? Phaser.Math.Easing.Linear.Linear : ease;
        parameters.duration = duration;
        parameters.onComplete = onComplete;
        parameters.t = 1;
        parameters.targets = follower;
        let existingUpdate = parameters.onUpdate;
        let segIdx = 0;
        let totalTime = 0;
        if(parameters.segments) parameters.segments.forEach((seg)=>{
            if(seg.normDuration) totalTime += seg.normDuration;
            else seg.normDuration = 1-totalTime;
        })
        parameters.onUpdate = ()=>{
            if(existingUpdate) existingUpdate();
            let pointPos = follower.t;

            if(parameters.segments) 
            {
                let segments = parameters.segments;
                let tweenTime = follower.t;
                let nextIdxTime = 0;

                for (let i = 0; i <= segIdx; i++) nextIdxTime += segments[i].normDuration;
                while (segments[segIdx] && tweenTime > nextIdxTime) {
                    segIdx++;
                    nextIdxTime += segments[segIdx].normDuration;
                }

                let seg = segments[segIdx];
                let segTime = seg.normDuration;
                let previousTime = nextIdxTime - segTime;
                let normalizedSegTime = (tweenTime - previousTime) / segTime;
                let normalizedEasedTime = seg.ease ? seg.ease(normalizedSegTime) * segTime : normalizedSegTime * segTime;
                
                pointPos = previousTime + normalizedEasedTime;
                pathTween.timeScale = seg.timeScale ? seg.timeScale : 1;
            }

            path.getPoint(pointPos, follower.vec);
            if(object instanceof Array) object.forEach((obj)=>{obj.setPosition(follower.vec.x, follower.vec.y);});
            else object.setPosition(follower.vec.x, follower.vec.y);
        };

        let pathTween = this.tweens.add(parameters);
        return pathTween;
    }

    killTween(object)
    {
        this.tweens.killTweensOf(object);
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addCirc(x, y, radius, color, parent = null)
    {
        let circ = this.add.graphics({ fillStyle: { color: color } });
        circ.circle = new Phaser.Geom.Circle(x, y, radius);
        circ.fillCircleShape(circ.circle);
        
        this.addToParent(circ, parent);
        return circ;
    }

    addEllipse(x, y, width, height, color, parent = null)
    {
        let ell = this.add.graphics({ fillStyle: { color: color } });
        ell.fillEllipseShape(new Phaser.Geom.Ellipse(0, -height * .5, width, height));
        ell.setPosition(x, y);
        this.addToParent(ell, parent);
        return ell;
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addRect(x, y, width, height, color, parent = null, setInteractive = false, listener = null, event = "pointerdown", isVisible = false)
    {
        let rec = this.add.graphics({ fillStyle: { color: color }});
        this.addToParent(rec, parent);
        rec.fillRectShape(new Phaser.Geom.Rectangle(x, y, width, height));
        rec.width = width;
        rec.height = height;

        if(setInteractive)
        {
            rec.alpha = isVisible ? 1 : .000001;
            rec.setInteractive({ useHandCursor: true, hitArea: new Phaser.Geom.Rectangle(x, y, width, height), hitAreaCallback: Phaser.Geom.Rectangle.Contains});
            rec.on(event, listener);
        }
        
        return rec;
    }

    addRoundedRect(x, y, width, height, color, radius, parent, setInteractive = false, onEvent = null, eventListener = "pointerdown", isVisible = false)
    {
        let graphics = this.add.graphics();
        this.addToParent(graphics, parent);
        graphics.fillStyle(color, 1);
        graphics.fillRoundedRect(x - (width * .5), y - (height * .5), width, height, {tl: radius, tr: radius, bl: radius, br: radius });
        graphics.width = width;
        graphics.height = height;
        if(setInteractive)
        {
            graphics.alpha = isVisible ? 1 : .000001;
            graphics.setInteractive(new Phaser.Geom.Rectangle(x - (width * .5), y - (height * .5), width, height), Phaser.Geom.Rectangle.Contains);
            graphics.on(eventListener, onEvent);
        }
        return graphics;
    }

    addToParent(object, parent) 
    {
        if(object instanceof Phaser.GameObjects.Particles.ParticleEmitterManager && parent instanceof Phaser.GameObjects.Container)
        {
            parent.add(object);
            return;
        }
        if(!parent || parent == this) this.add.existing(object);
        else parent.add(object);
    }

    get size()
    {
        return {width: this.game.config.width, height: this.game.config.height};
    }

    get mid()
    {
        return {x: this.game.config.width * .5, y: this.game.config.height * .5};
    }

    /**
     * @return {Phaser.Sound.HTML5AudioSound}
     */
    playSound(sound, volume = 1, repeat = false)
    {
        let gameAudio = this.sound.addAudioSprite(this.defaultAudioSprite);

        gameAudio.play(sound);
        gameAudio.volume = volume;
        gameAudio.loop = repeat;

        return gameAudio;
    }

    addImageBody(x, y, frame, options, angle)
    {
        this.addBody(x, y, this.addImage(0, 0, frame), options, angle);
    }

    stopSounds()
    {
        this.sound.stopAll();
    }

    addShadow(x, y, width, height)
    {
        return this.addEllipse(x, y, width, height, 0xbfb4ff).setBlendMode(Phaser.BlendModes.MULTIPLY);
    }

    update()
    {

    }
}